// src/pages/LicenseCollectRequests.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { licensesAPI, LicenseCollectRequest } from '@/services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    Loader2,
    Filter as FilterIcon,
    Search as SearchIcon,
    RefreshCw,
    ChevronDown,
    ChevronUp,
    ChevronRight,
    ChevronLeft,
    ListCollapse,
    ListStart,
    Clock,
    CheckCircle2,
    XCircle,
    Sparkles,
    Info,
} from 'lucide-react';

const fa = new Intl.NumberFormat('fa-IR');
const faDateTime = (d?: string | number | Date | null) =>
    d ? new Date(d).toLocaleString('fa-IR') : '—';

const statusLabel = (s: LicenseCollectRequest['status']) => {
    switch (s) {
        case 'pending':
            return 'در انتظار بررسی';
        case 'processed':
            return 'انجام شد';
        case 'rejected':
            return 'رد شده';
        default:
            return s;
    }
};

const statusBadgeVariant = (s: LicenseCollectRequest['status']) => {
    switch (s) {
        case 'pending':
            return 'outline' as const;
        case 'processed':
            return 'default' as const;
        case 'rejected':
            return 'destructive' as const;
        default:
            return 'outline' as const;
    }
};

const statusColorClass = (s: LicenseCollectRequest['status']) => {
    switch (s) {
        case 'pending':
            return 'text-amber-700 bg-amber-50 border-amber-200';
        case 'processed':
            return 'text-emerald-700 bg-emerald-50 border-emerald-200';
        case 'rejected':
            return 'text-red-700 bg-red-50 border-red-200';
        default:
            return 'text-gray-700 bg-gray-50 border-gray-200';
    }
};

const markMatch = (text: string, q: string) => {
    if (!q) return text;
    try {
        const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
        return (
            <>
                {parts.map((p, i) =>
                    p.toLowerCase() === q.toLowerCase() ? (
                        <mark key={i} className="bg-yellow-200 px-0.5 rounded">
                            {p}
                        </mark>
                    ) : (
                        <span key={i}>{p}</span>
                    ),
                )}
            </>
        );
    } catch {
        return text;
    }
};

const LicenseCollectRequestsPage: React.FC = () => {
    const [items, setItems] = useState<LicenseCollectRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');

    const [filtersOpen, setFiltersOpen] = useState(false);
    const [q, setQ] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | LicenseCollectRequest['status']>('all');

    const [expanded, setExpanded] = useState<Set<number>>(new Set());

    // pagination (client-side)
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const loadData = async () => {
        try {
            setError('');
            setLoading(true);
            const data = await licensesAPI.getCollectRequests();
            setItems(data || []);
        } catch (e: any) {
            setError(e?.message || 'خطا در دریافت درخواست‌های جمع‌آوری');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleRefresh = async () => {
        try {
            setRefreshing(true);
            await loadData();
        } finally {
            setRefreshing(false);
        }
    };

    const toggleExpanded = (id: number) => {
        setExpanded(prev => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id);
            else n.add(id);
            return n;
        });
    };

    const expandAll = () => setExpanded(new Set(items.map(i => i.id)));
    const collapseAll = () => setExpanded(new Set());

    const stats = useMemo(() => {
        const total = items.length;
        const pending = items.filter(i => i.status === 'pending').length;
        const processed = items.filter(i => i.status === 'processed').length;
        const rejected = items.filter(i => i.status === 'rejected').length;
        return { total, pending, processed, rejected };
    }, [items]);

    const filtered = useMemo(() => {
        const text = q.trim().toLowerCase();
        return items.filter(i => {
            const textMatch =
                !text ||
                [
                    i.license_key,
                    i.product_title,
                    String(i.product_id),
                    i.status,
                    i.note || '',
                ]
                    .filter(Boolean)
                    .some(t => String(t).toLowerCase().includes(text));

            const statusMatch =
                statusFilter === 'all' ? true : i.status === statusFilter;

            return textMatch && statusMatch;
        });
    }, [items, q, statusFilter]);

    const totalItems = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [totalPages, page]);

    const pageSlice = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filtered.slice(start, start + pageSize);
    }, [filtered, page, pageSize]);

    /* ---------- Render ---------- */
    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gradient-to-br from-emerald-50 via-emerald-50 to-teal-50" dir="rtl">
                <div className="text-center">
                    <Loader2 className="h-12 w-12 animate-spin text-emerald-600 mx-auto mb-4" />
                    <p className="text-emerald-700 font-medium">در حال بارگذاری درخواست‌ها...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6" dir="rtl">
                <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
                <div className="mt-3">
                    <Button variant="outline" onClick={handleRefresh}>
                        <RefreshCw className="ml-2 h-4 w-4" />
                        تلاش مجدد
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-h-screen space-y-6 p-4 md:p-8 bg-gradient-to-br from-emerald-50 via-emerald-50 to-teal-50"
            dir="rtl"
        >
            {/* Header */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 via-emerald-600 to-teal-600 p-8 shadow-2xl">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-15" />
                <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="text-white space-y-2">
                        <div className="flex items-center gap-3">
                            <Sparkles className="h-8 w-8" />
                            <h1 className="text-2xl md:text-4xl font-bold">درخواست‌های جمع‌آوری لایسنس</h1>
                        </div>
                        <p className="text-emerald-100 text-md">
                            در این بخش می‌توانید وضعیت درخواست‌های لغو / جمع‌آوری لایسنس‌های ثبت شده را ببینید.
                        </p>
                    </div>
                    <div className="flex flex-wrap w-auto mx-auto justify-end gap-3">
                        <div className="bg-white/15 backdrop-blur-sm rounded-xl px-5 py-3 text-white border border-white/30 min-w-[120px]">
                            <div className="text-xs opacity-90">کل درخواست‌ها</div>
                            <div className="text-2xl font-bold">{fa.format(stats.total)}</div>
                        </div>
                        <div className="bg-white/15 backdrop-blur-sm rounded-xl px-5 py-3 text-white border border-white/30 min-w-[120px]">
                            <div className="text-xs opacity-90">در انتظار بررسی</div>
                            <div className="text-2xl font-bold text-amber-200">
                                {fa.format(stats.pending)}
                            </div>
                        </div>
                        <div className="bg-white/15 backdrop-blur-sm rounded-xl px-5 py-3 text-white border border-white/30 min-w-[120px]">
                            <div className="text-xs opacity-90">انجام شده</div>
                            <div className="text-2xl font-bold text-emerald-200">
                                {fa.format(stats.processed)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <Card className="border-emerald-200/60 shadow-lg bg-white/85 backdrop-blur-sm">
                <CardHeader className="py-4 bg-gradient-to-r from-emerald-50 to-emerald-50/70">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-xl bg-emerald-600 flex items-center justify-center shadow-sm">
                                <FilterIcon className="h-5 w-5 text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-emerald-900 text-lg">فیلترها و جستجو</CardTitle>
                                <CardDescription className="text-emerald-700">
                                    جستجو و فیلتر روی درخواست‌های جمع‌آوری لایسنس
                                </CardDescription>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <Button
                                variant="outline"
                                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                onClick={() => setFiltersOpen(v => !v)}
                            >
                                {filtersOpen ? (
                                    <ListCollapse className="ml-2 h-4 w-4" />
                                ) : (
                                    <ListStart className="ml-2 h-4 w-4" />
                                )}
                                {filtersOpen ? 'بستن فیلترها' : 'نمایش فیلترها'}
                            </Button>
                            <Button
                                variant="outline"
                                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                onClick={handleRefresh}
                                disabled={refreshing}
                            >
                                <RefreshCw className={`ml-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                                به‌روزرسانی
                            </Button>
                        </div>
                    </div>
                </CardHeader>

                {filtersOpen && (
                    <CardContent className="space-y-4 pt-6">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                            <div className="space-y-2 lg:col-span-2">
                                <Label className="text-emerald-900 font-medium">جستجو</Label>
                                <div className="relative">
                                    <SearchIcon className="absolute left-3 top-3.5 h-5 w-5 text-emerald-300" />
                                    <Input
                                        value={q}
                                        onChange={e => {
                                            setQ(e.target.value);
                                            setPage(1);
                                        }}
                                        placeholder="جستجو در کلید لایسنس، نام محصول، وضعیت یا توضیحات..."
                                        className="pl-11 h-11 border-emerald-200 focus:ring-emerald-500 focus:border-emerald-500 rounded-xl"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-emerald-900 font-medium">وضعیت</Label>
                                <select
                                    value={statusFilter}
                                    onChange={e => {
                                        setStatusFilter(e.target.value as any);
                                        setPage(1);
                                    }}
                                    className="h-11 w-full rounded-xl border border-emerald-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                >
                                    <option value="all">🔘 همه</option>
                                    <option value="pending">⏳ در انتظار</option>
                                    <option value="processed">✅ انجام شده</option>
                                    <option value="rejected">❌ رد شده</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex justify-between items-center gap-3 text-xs text-emerald-700 bg-emerald-50/60 border border-emerald-100 rounded-xl px-3 py-2">
                            <div className="flex items-center gap-2">
                                <Info className="h-4 w-4" />
                                <span>
                  درخواست جمع‌آوری معمولاً بعد از بررسی پشتیبانی پردازش شده و وضعیت آن در این صفحه نمایش داده می‌شود.
                </span>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-emerald-700 hover:bg-emerald-100"
                                onClick={() => {
                                    setQ('');
                                    setStatusFilter('all');
                                    setPage(1);
                                }}
                            >
                                ریست فیلترها
                            </Button>
                        </div>
                    </CardContent>
                )}
            </Card>

            {/* List */}
            {items.length === 0 ? (
                <Card className="shadow-lg border-emerald-500 bg-white/80 backdrop-blur-sm">
                    <CardContent className="text-center py-16">
                        <Clock className="h-16 w-16 mx-auto mb-4 text-emerald-200" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            هنوز هیچ درخواستی ثبت نشده است
                        </h3>
                        <p className="text-gray-500">
                            وقتی برای لایسنس‌های خود درخواست جمع‌آوری ثبت کنید، اینجا نمایش داده می‌شوند.
                        </p>
                    </CardContent>
                </Card>
            ) : filtered.length === 0 ? (
                <Card className="shadow-lg border-emerald-500 bg-white/80 backdrop-blur-sm">
                    <CardContent className="text-center py-12">
                        <XCircle className="h-12 w-12 mx-auto mb-3 text-amber-300" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            موردی با این فیلترها پیدا نشد
                        </h3>
                        <p className="text-gray-500 mb-3">فیلترها یا متن جستجو را تغییر دهید.</p>
                        <Button
                            variant="outline"
                            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            onClick={() => {
                                setQ('');
                                setStatusFilter('all');
                                setPage(1);
                            }}
                        >
                            ریست فیلترها
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-4">
                    {pageSlice.map(req => {
                        const isExpanded = expanded.has(req.id);
                        return (
                            <div
                                key={req.id}
                                className="relative overflow-hidden rounded-2xl border border-emerald-500 bg-gradient-to-br from-emerald-50/70 to-white shadow-md hover:shadow-xl transition-all duration-300"
                            >
                                <div className="flex flex-col md:flex-row md:items-stretch">
                                    {/* main info */}
                                    <div className="flex-1 p-4 md:p-5 flex flex-col justify-between border-b md:border-b-0 md:border-l border-dashed border-emerald-100">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-right">
                                            {/* license & product */}
                                            <div className="space-y-2">
                                                <div className="text-[11px] text-gray-500 font-semibold">
                                                    لایسنس و محصول
                                                </div>
                                                <div
                                                    className="text-sm font-mono text-emerald-800 bg-emerald-50 inline-flex px-2 py-1 rounded-md border border-emerald-100"
                                                    dir="ltr"
                                                >
                                                    {markMatch(req.license_key, q.trim())}
                                                </div>
                                                <div className="mt-1 text-[11px] text-gray-500">
                                                    محصول:{' '}
                                                    <span className="font-semibold text-gray-900">
                                                        {req.product_title || 'محصول بدون نام'}
                                                    </span>
                                                </div>
                                                {/* شناسه لایسنس حذف شد */}
                                            </div>

                                            {/* status */}
                                            <div className="space-y-2">
                                                <div className="text-[11px] text-gray-500 font-semibold">
                                                    وضعیت درخواست
                                                </div>
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <Badge
                                                        variant={statusBadgeVariant(req.status)}
                                                        className={`text-xs px-3 py-1 rounded-full border ${statusColorClass(
                                                            req.status,
                                                        )}`}
                                                    >
                                                        {statusLabel(req.status)}
                                                    </Badge>
                                                </div>
                                                <div className="mt-2 text-[11px] text-gray-500">
                                                    تاریخ ثبت:{' '}
                                                    <span className="font-semibold text-gray-900">
                            {faDateTime(req.created_at)}
                          </span>
                                                </div>
                                            </div>

                                            {/* actions */}
                                            <div className="space-y-2">
                                                <div className="text-[11px] text-gray-500 font-semibold">
                                                    جزئیات بیشتر
                                                </div>
                                                <Button
                                                    variant="outline"
                                                    className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50 flex items-center justify-center gap-2"
                                                    onClick={() => toggleExpanded(req.id)}
                                                >
                                                    {isExpanded ? (
                                                        <ChevronUp className="h-4 w-4" />
                                                    ) : (
                                                        <ChevronDown className="h-4 w-4" />
                                                    )}
                                                    مشاهده توضیحات
                                                </Button>

                                                {req.status === 'pending' && (
                                                    <div className="mt-1 flex items-center gap-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1">
                                                        <Clock className="h-3.5 w-3.5" />
                                                        <span>در انتظار بررسی تیم پشتیبانی</span>
                                                    </div>
                                                )}
                                                {req.status === 'processed' && (
                                                    <div className="mt-1 flex items-center gap-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-2 py-1">
                                                        <CheckCircle2 className="h-3.5 w-3.5" />
                                                        <span>این درخواست پردازش شده است.</span>
                                                    </div>
                                                )}
                                                {req.status === 'rejected' && (
                                                    <div className="mt-1 flex items-center gap-2 text-[11px] text-red-700 bg-red-50 border border-red-100 rounded-lg px-2 py-1">
                                                        <XCircle className="h-3.5 w-3.5" />
                                                        <span>این درخواست رد شده است.</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* extra note */}
                                {isExpanded && (
                                    <div className="border-t border-dashed border-emerald-100 bg-gray-50/70 px-4 py-3">
                                        <div className="flex items-start gap-2 text-[13px] text-gray-800">
                                            <Info className="h-4 w-4 mt-0.5 text-emerald-600" />
                                            <div>
                                                <div className="font-semibold mb-1">توضیحات کاربر</div>
                                                <div className="whitespace-pre-line">
                                                    {req.note && req.note.trim().length > 0
                                                        ? req.note
                                                        : 'هیچ توضیحی ثبت نشده است.'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* pagination */}
                    <Card className="border-emerald-200/60 shadow-lg bg-white/85 backdrop-blur-sm">
                        <CardContent className="py-4">
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <span className="text-sm text-gray-600 font-medium">نمایش:</span>
                                    <select
                                        value={pageSize}
                                        onChange={e => {
                                            setPageSize(parseInt(e.target.value, 10));
                                            setPage(1);
                                        }}
                                        className="h-10 rounded-xl border border-emerald-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                    >
                                        <option value={5}>۵ مورد</option>
                                        <option value={10}>۱۰ مورد</option>
                                        <option value={20}>۲۰ مورد</option>
                                        <option value={50}>۵۰ مورد</option>
                                    </select>
                                </div>

                                <div className="text-sm text-gray-700 text-center font-medium bg-emerald-50 px-4 py-2 rounded-xl border border-emerald-100">
                                    صفحه{' '}
                                    <span className="text-emerald-700 font-bold">{fa.format(page)}</span>{' '}
                                    از{' '}
                                    <span className="text-emerald-700 font-bold">{fa.format(totalPages)}</span>
                                    {' — '}
                                    <span className="text-emerald-700 font-bold">
                    {fa.format(totalItems)}
                  </span>{' '}
                                    درخواست
                                </div>

                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page <= 1}
                                        className="flex-1 sm:flex-none border-emerald-400 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 transition-all rounded-xl"
                                    >
                                        <ChevronRight className="ml-1 h-4 w-4" />
                                        قبلی
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        disabled={page >= totalPages}
                                        className="flex-1 sm:flex-none border-emerald-400 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 transition-all rounded-xl"
                                    >
                                        بعدی
                                        <ChevronLeft className="mr-1 h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default LicenseCollectRequestsPage;
